import { Button } from "@izicvs/ui";
import { t } from "@lingui/macro";
import { MagicWand, Plus } from "@phosphor-icons/react";

import { useAiUsage } from "@/client/services/ai/usage";
import { useBuyTokens, useSubscription } from "@/client/services/billing";

const formatTokens = (n: number) => (n >= 1000 ? `${Math.round(n / 1000)}k` : `${n}`);

export const OpenAISettings = () => {
  const { usage, loading } = useAiUsage();
  const { subscription } = useSubscription();
  const { purchase, loading: buying } = useBuyTokens();

  const usedTokens = usage?.usedTokens ?? 0;
  const tokenBudget = usage?.tokenBudget ?? 0;
  const tokenBalance = usage?.tokenBalance ?? 0;
  const viaOrganization = usage?.viaOrganization ?? false;
  const unlimited = tokenBudget <= 0;
  const percent = tokenBudget > 0 ? Math.min(100, Math.round((usedTokens / tokenBudget) * 100)) : 0;

  // Individuals can top up their own balance; org members are covered by the
  // organization's shared pool, so no personal purchase there.
  const canBuy = Boolean(subscription?.billingEnabled) && !viaOrganization;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="flex items-center gap-x-2 text-2xl font-bold leading-relaxed tracking-tight">
          <MagicWand />
          {t`AI Assistant`}
        </h3>
        <p className="leading-relaxed opacity-75">
          {t`Improve your writing, fix spelling and grammar, and change the tone of your text with AI while composing your resume. It runs on our servers, so there is nothing to set up.`}
        </p>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">{t`Monthly usage`}</p>
          {canBuy && (
            <Button size="sm" variant="outline" disabled={buying} onClick={() => void purchase(1)}>
              <Plus className="mr-1.5 size-4" />
              {t`Buy tokens`}
            </Button>
          )}
        </div>

        {loading ? (
          <p className="mt-1 text-sm opacity-60">{t`Loading...`}</p>
        ) : unlimited ? (
          <p className="mt-1 text-sm opacity-75">{t`You have unlimited AI actions.`}</p>
        ) : (
          <>
            <p className="mt-1 text-sm opacity-75">
              {t`${usedTokens.toLocaleString()} of ${tokenBudget.toLocaleString()} AI tokens used this month.`}
            </p>
            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-secondary">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${percent}%` }}
              />
            </div>
            {tokenBalance > 0 && (
              <p className="mt-3 text-sm opacity-75">
                {t`Plus ${formatTokens(tokenBalance)} purchased tokens available.`}
              </p>
            )}
            {viaOrganization ? (
              <p className="mt-3 text-xs opacity-60">
                {t`Your AI usage is covered by your organization's shared token pool.`}
              </p>
            ) : (
              canBuy && (
                <p className="mt-3 text-xs opacity-60">
                  {t`Out of tokens? Buy a pack to keep going. Purchased tokens never expire.`}
                </p>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};
